import { useMemo, useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ProjectCreateWizardProvider } from './ProjectCreateWizardContext'
import { ProjectCreateProgress } from './ProjectCreateProgress'
import { Step1ClientSearch } from './steps/Step1ClientSearch'
import { Step2ClientDetails } from './steps/Step2ClientDetails'
import { Step2ExistingClientJobSite } from './steps/Step2ExistingClientJobSite'
import { Step3ProjectDetails } from './steps/Step3ProjectDetails'
import { useProjectCreateWizard } from './ProjectCreateWizardContext'
import { useProjectCreateCatalogV2 } from '@/hooks/useProjectCreateCatalogV2'
import { LoadingSpinner } from '@/components/features/LoadingSpinner'
import { DuplicateClientsModal } from './DuplicateClientsModal'
import { createProjectV2, DuplicateDetectionException, type DuplicateClientDto, type CreateProjectV2RequestDto } from '@/lib/api/v2/projects'
import { getClientDetailsV2 } from '@/lib/api/v2/clients'
import { getJobSiteDetailsV2 } from '@/lib/api/v2/job-sites'
import { showErrorToast } from '@/lib/toast'

function WizardContent() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    draft,
    setSelectedExistingClientId,
    setSelectedExistingClientDisplayName,
    setSelectedJobSiteId,
    setSelectedJobSiteLabel,
    setIsNewJobSite,
    setIsNewClient,
    setProjectField,
    setClientField,
  } = useProjectCreateWizard()
  const [duplicateClients, setDuplicateClients] = useState<DuplicateClientDto[]>([])
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [pendingPayload, setPendingPayload] = useState<CreateProjectV2RequestDto | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [initializedFromUrl, setInitializedFromUrl] = useState(false)

  const {
    projectTypeOptions,
    languageOptions,
    civilityOptions,
    isLoading: catalogLoading,
    error: catalogError,
  } = useProjectCreateCatalogV2()

  // Seed the default document language from the org's supported list (first entry = org primary)
  // once the catalog API has resolved. Skips if user already touched the radio (non-empty value).
  useEffect(() => {
    if (!languageOptions.length) return
    if (draft.client.documentLanguageId) return
    setClientField('documentLanguageId', languageOptions[0].id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languageOptions])

  // Initialize from URL params (e.g., from client detail page)
  const customerIdParam = searchParams.get('customerId')
  const jobSiteIdParam = searchParams.get('jobSiteId')
  const modeParam = searchParams.get('mode')

  // Pre-fill customer and optional job site if provided in URL
  useEffect(() => {
    if (modeParam === 'direct-sale') {
      setProjectField('isDirectSale', true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (customerIdParam && !initializedFromUrl) {
      setSelectedExistingClientId(customerIdParam)
      setIsNewClient(false)
      setInitializedFromUrl(true)

      // Fetch client details to get display name
      getClientDetailsV2(customerIdParam)
        .then((client) => {
          setSelectedExistingClientDisplayName(client.name || 'Client')
        })
        .catch((error) => {
          console.error('Error fetching client details:', error)
          setSelectedExistingClientDisplayName('Client')
        })

      // If jobSiteId provided, pre-select it and jump to step 3
      if (jobSiteIdParam) {
        setSelectedJobSiteId(jobSiteIdParam)
        setIsNewJobSite(false)

        getJobSiteDetailsV2(jobSiteIdParam)
          .then((jobSite) => {
            setSelectedJobSiteLabel(jobSite.label || jobSite.address?.city || 'Chantier')
          })
          .catch((error) => {
            console.error('Error fetching job site details:', error)
            setSelectedJobSiteLabel('Chantier')
          })
      }

      // Redirect to appropriate step if not already set
      const targetStep = jobSiteIdParam ? '3' : '2'
      if (!searchParams.get('step')) {
        const params = new URLSearchParams(searchParams)
        params.set('step', targetStep)
        navigate(`/projects/new?${params.toString()}`, { replace: true })
      }
    }
  }, [
    customerIdParam,
    jobSiteIdParam,
    initializedFromUrl,
    setSelectedExistingClientId,
    setSelectedExistingClientDisplayName,
    setSelectedJobSiteId,
    setSelectedJobSiteLabel,
    setIsNewJobSite,
    setIsNewClient,
    navigate,
    searchParams,
  ])

  const stepParam = searchParams.get('step')
  const currentStep = useMemo(() => {
    const parsed = stepParam ? parseInt(stepParam, 10) : 1
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 3) return parsed
    return 1
  }, [stepParam])

  const totalSteps = 3

  const goToStep = (step: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('step', String(step))
    navigate(`/projects/new?${params.toString()}`)
  }

  const goToPreviousStep = () => {
    if (currentStep <= 1) return
    goToStep(currentStep - 1)
  }

  const handleCreate = async () => {
    if (!draft.project.isDirectSale && !draft.project.projectTypeId) return

    const projectName = draft.project.projectName.trim() || null

    const payload: CreateProjectV2RequestDto = {
      projectTypeId: draft.project.projectTypeId ?? '00000000-0000-0000-0000-000000000000',
      salesOwnerId: null,
      allowDuplicate: false,
      projectName,
      isDirectSale: draft.project.isDirectSale || undefined,
      vatPercent: draft.project.isDirectSale ? draft.project.vatPercent : undefined,
      customer: draft.isNewClient
        ? {
            mode: 2,
            customerId: null,
            new: {
              type: draft.client.clientType === 'professionnel' ? 2 : 1,
              name: draft.client.name.trim() || null,
              designation: draft.client.clientType === 'particulier' && draft.client.designation.trim()
                ? draft.client.designation.trim()
                : null,
              vatNumber: null,
              companyRegistrationNumber: null,
              preferredLanguageId: draft.client.documentLanguageId,
              primaryContact: {
                civility: Number.isFinite(Number(draft.client.civilityId)) ? Number(draft.client.civilityId) : 1,
                firstName: null,
                lastName: draft.client.lastName,
                phone: draft.client.phone,
                email: draft.client.email || null,
              },
            },
          }
        : {
            mode: 1,
            customerId: draft.selectedExistingClientId,
            new: undefined,
          },
      jobSite: draft.isNewClient
        ? {
            mode: 0,
            jobSiteId: null,
            new: undefined,
          }
        : draft.isNewJobSite
          ? {
              mode: 0,
              jobSiteId: null,
              new: undefined,
            }
          : {
              mode: 1,
              jobSiteId: draft.selectedJobSiteId,
              new: undefined,
            },
    }

    setIsCreating(true)
    try {
      const response = await createProjectV2(payload)

      // Direct sale → go straight to order editor to add lines
      if (draft.project.isDirectSale) {
        navigate(`/projects/${response.projectId}/commande-directe`)
        return
      }

      // If no job site configured → redirect to job site creation, with project page as back destination
      // If job site already known → go to project configuration
      if (!response.jobSiteId) {
        navigate(`/projects/${response.projectId}/job-site/new`, {
          state: {
            from: `/projects/${response.projectId}`,
            customerId: response.customerId,
            hasBillingAddress: draft.isNewClient ? false : undefined,
          },
        })
      } else {
        navigate(`/projects/${response.projectId}/configuration`)
      }
    } catch (error) {
      if (error instanceof DuplicateDetectionException) {
        // Show duplicate detection modal
        setDuplicateClients(error.data.duplicates.clients.items)
        setPendingPayload(payload)
        setShowDuplicateModal(true)
      } else {
        console.error('Error creating project:', error)
        showErrorToast('Erreur lors de la création du projet')
      }
    } finally {
      setIsCreating(false)
    }
  }

  const handleCreateWithDuplicates = async () => {
    if (!pendingPayload) return

    setIsCreating(true)
    try {
      // Retry with allowDuplicate: true
      const response = await createProjectV2({
        ...pendingPayload,
        allowDuplicate: true,
      })

      // Direct sale → go straight to order editor to add lines
      if (draft.project.isDirectSale) {
        navigate(`/projects/${response.projectId}/commande-directe`)
        return
      }

      // If no job site configured → redirect to job site creation, with project page as back destination
      // If job site already known → go to project configuration
      if (!response.jobSiteId) {
        navigate(`/projects/${response.projectId}/job-site/new`, {
          state: {
            from: `/projects/${response.projectId}`,
            customerId: response.customerId,
            hasBillingAddress: draft.isNewClient ? false : undefined,
          },
        })
      } else {
        navigate(`/projects/${response.projectId}/configuration`)
      }
    } catch (error) {
      console.error('Error creating project with duplicates:', error)
      showErrorToast('Erreur lors de la création du projet')
      setIsCreating(false)
    }
  }

  const handleCancelDuplicates = () => {
    setShowDuplicateModal(false)
    setDuplicateClients([])
    setPendingPayload(null)
  }

  // SOLY-CUST-001 — reuse the proposed client instead of creating a duplicate. This is the only way
  // back to a client whose projects are all in the trash: the client search hides it.
  const handleUseExistingClient = (customerId: string, name: string) => {
    handleCancelDuplicates()
    setSelectedExistingClientId(customerId)
    setSelectedExistingClientDisplayName(name)
    setIsNewClient(false)
    setSelectedJobSiteId(null)
    setIsNewJobSite(false)
    goToStep(2)
  }

  const stepLabels = useMemo(() => {
    return ['Client', draft.isNewClient ? 'Coordonnées' : 'Chantier', 'Projet']
  }, [draft.isNewClient])

  if (catalogLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    )
  }

  if (catalogError) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="bg-card border border-border rounded-lg p-6 md:p-8 text-center text-muted-foreground">
          Erreur de chargement des options
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <ProjectCreateProgress currentStep={currentStep} totalSteps={totalSteps} stepLabels={stepLabels} />

      <div className="bg-card border border-border rounded-lg p-6 md:p-8">
        {currentStep === 1 && <Step1ClientSearch onNext={() => goToStep(2)} />}

        {currentStep === 2 &&
          (draft.isNewClient ? (
            <Step2ClientDetails
              currentStep={currentStep}
              onNext={() => goToStep(3)}
              onBack={() => goToPreviousStep()}
              languageOptions={languageOptions}
              civilityOptions={civilityOptions}
            />
          ) : draft.selectedExistingClientId ? (
            <Step2ExistingClientJobSite
              currentStep={currentStep}
              onNext={() => goToStep(3)}
              onBack={() => goToPreviousStep()}
              customerId={draft.selectedExistingClientId || ''}
              clientDisplayName={draft.selectedExistingClientDisplayName || 'Client'}
            />
          ) : null)}
        {currentStep === 3 && (
          <Step3ProjectDetails
            currentStep={currentStep}
            onBack={goToPreviousStep}
            onCreate={handleCreate}
            projectTypeOptions={projectTypeOptions}
            clientSummary={draft.selectedExistingClientDisplayName || null}
            jobSiteSummary={
              draft.isNewClient
                ? null
                : draft.isNewJobSite
                  ? 'Nouveau chantier'
                  : draft.selectedJobSiteLabel
            }
            existingProjectsAtAddress={[]}
          />
        )}
      </div>

      {/* Duplicate clients detection modal */}
      <DuplicateClientsModal
        open={showDuplicateModal}
        duplicates={duplicateClients}
        onCancel={handleCancelDuplicates}
        onCreateAnyway={handleCreateWithDuplicates}
        onUseExisting={handleUseExistingClient}
        isCreating={isCreating}
      />
    </div>
  )
}

export function ProjectCreateWizard() {
  return (
    <ProjectCreateWizardProvider>
      <WizardContent />
    </ProjectCreateWizardProvider>
  )
}
